import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { Country, FormStep, RoutesPath } from '@features/succession/enums';
import { ReporterInformationForm } from '@features/succession/forms';
import { CustomerInformation, ReporterInformation } from '@features/succession/models';
import { SuccessionFacade } from '@features/succession/succession.facade';
import { ControlValidators } from '@shared/validators';

@Injectable({ providedIn: 'root' })
export class ReporterInformationFacade {
  protected destroyRef = inject(DestroyRef);

  private readonly successionFacade = inject(SuccessionFacade);

  private readonly formBuilder = inject(FormBuilder);

  public reporterInformationForm: FormGroup<ReporterInformationForm> = this.formBuilder.group(
    {
      gender: ['', Validators.required],
      name: ['', Validators.required],
      birthname: [''],
      firstname: ['', Validators.required],
      relationshipWithDeceased: ['', Validators.required],
      address: ['', Validators.required],
      complementaryAddress: [''],
      zipcode: ['', Validators.required],
      city: ['', Validators.required],
      country: ['', Validators.required],
      email: [
        '',
        [
          Validators.required,
          ControlValidators.emailFormatValidator(),
          ControlValidators.noSpecialCharactersValidator(),
        ],
      ],
      emailConfirmation: [
        '',
        [ControlValidators.emailMatchValidator('email'), Validators.required],
      ],
      phoneNumber: ['', Validators.required],
      commentary: [''],
    },
    { validators: ControlValidators.emailsMatchValidator() },
  );

  constructor() {
    this.registerCountriesValidators();
    this.setRegistrantInformationFormDefaultValues();
  }

  public get gender(): FormControl<string | null> {
    return this.reporterInformationForm.controls.gender;
  }

  public get name(): FormControl<string | null> {
    return this.reporterInformationForm.controls.name;
  }

  public get birthname(): FormControl<string | null> {
    return this.reporterInformationForm.controls.birthname;
  }

  public get firstname(): FormControl<string | null> {
    return this.reporterInformationForm.controls.firstname;
  }

  public get relationshipWithDeceased(): FormControl<string | null> {
    return this.reporterInformationForm.controls.relationshipWithDeceased;
  }

  public get address(): FormControl<string | null> {
    return this.reporterInformationForm.controls.address;
  }

  public get complementaryAddress(): FormControl<string | null> {
    return this.reporterInformationForm.controls.complementaryAddress;
  }

  public get zipcode(): FormControl<string | null> {
    return this.reporterInformationForm.controls.zipcode;
  }

  public get city(): FormControl<string | null> {
    return this.reporterInformationForm.controls.city;
  }

  public get country(): FormControl<string | null> {
    return this.reporterInformationForm.controls.country;
  }

  public get email(): FormControl<string | null> {
    return this.reporterInformationForm.controls.email;
  }

  public get emailConfirmation(): FormControl<string | null> {
    return this.reporterInformationForm.controls.emailConfirmation;
  }

  public get phoneNumber(): FormControl<string | null> {
    return this.reporterInformationForm.controls.phoneNumber;
  }

  public get commentary(): FormControl<string | null> {
    return this.reporterInformationForm.controls.commentary;
  }

  private setRegistrantInformationFormDefaultValues(): void {
    this.successionFacade.customerInformation$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((customerInformation: CustomerInformation) => {
        this.reporterInformationForm.reset(customerInformation.reporterInformations);
      });
  }

  private registerCountriesValidators(): void {
    this.country.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value?: string | null) => {
        if (value === Country.FRANCE) {
          this.zipcode.addValidators(Validators.required);
          this.city.addValidators(Validators.required);
        }

        if (value !== Country.FRANCE) {
          this.zipcode.removeValidators(Validators.required);
          this.city.removeValidators(Validators.required);
        }

        this.zipcode.updateValueAndValidity();
        this.city.updateValueAndValidity();
      });
  }

  private navigateToPage(route: RoutesPath): void {
    const reporterInformation: ReporterInformation = {
      gender: this.gender.value || '',
      name: this.name.value || '',
      birthname: this.birthname.value || '',
      firstname: this.firstname.value || '',
      relationshipWithDeceased: this.relationshipWithDeceased.value || '',
      address: this.address.value || '',
      complementaryAddress: this.complementaryAddress.value || '',
      zipcode: this.zipcode.value || '',
      city: this.city.value || '',
      country: this.country.value || '',
      email: this.email.value || '',
      emailConfirmation: this.emailConfirmation.value || '',
      phoneNumber: this.phoneNumber.value || '',
      commentary: `${this.commentary.value?.replace(/\n/g, ' ')}` || '',
    };
    const customerInformation: CustomerInformation = {
      token: this.successionFacade.token,
      savePage: FormStep.THREE,
      loadPage: route === RoutesPath.PAGE_2 ? FormStep.TWO : FormStep.FOUR,
      reporterInformations: reporterInformation,
      deceasedInformations: undefined,
      deceasedMoreInformations: undefined,
      notaryInformations: undefined,
      inheritanceInformations: undefined,
    };
    this.successionFacade.saveAndLoad(customerInformation);
  }

  public onPrevious(): void {
    this.navigateToPage(RoutesPath.PAGE_2);
    this.successionFacade.trackPrevious();
  }

  public onNext(): void {
    this.reporterInformationForm.markAllAsTouched();
    if (this.reporterInformationForm.valid) {
      this.navigateToPage(RoutesPath.PAGE_4);
      this.successionFacade.trackNext();
    }
  }
}
